import { ColorRank } from "../types/battle"
import {
  DailyRankSummary,
  DailyTournamentCurrent,
  DailyTournamentEntry,
  DailyTournamentHistoryItem,
  DailyTournamentStatus,
} from "../types/dailyVirtualTournament"
import { normalizeColorRank } from "./radarStatus"
import { normalizeGameCard } from "./gameCards"
import {
  normalizeVirtualTournamentDetail,
  formatVirtualTournamentRankLabel,
} from "./virtualTournamentNormalize"

const DAILY_STATUSES: DailyTournamentStatus[] = [
  "DRAFT",
  "REGISTRATION",
  "BRACKET_READY",
  "IN_PROGRESS",
  "FINISHED",
  "CANCELLED",
]

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1" || value === "true") return true
  if (value === 0 || value === "0" || value === "false") return false
  return fallback
}

function pickStatus(value: unknown): DailyTournamentStatus {
  const raw = pickString(value).toUpperCase()
  return DAILY_STATUSES.includes(raw as DailyTournamentStatus)
    ? (raw as DailyTournamentStatus)
    : "REGISTRATION"
}

function normalizeEntry(raw: unknown): DailyTournamentEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const participantId = pickString(
    obj.participantId ?? obj.participant_id ?? obj.id
  )
  const cardRaw = obj.card ?? obj.gameCard ?? obj.game_card
  const cardId = pickString(
    obj.cardId ??
      obj.card_id ??
      (cardRaw && typeof cardRaw === "object" && !Array.isArray(cardRaw)
        ? (cardRaw as Record<string, unknown>).id
        : undefined)
  )
  const card = normalizeGameCard(cardRaw)
  if (!participantId || !cardId) return null
  return { participantId, cardId, card: card ?? null }
}

export function normalizeDailyRankSummary(raw: unknown): DailyRankSummary | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)
  const tournamentId = pickString(obj.tournamentId ?? obj.tournament_id ?? obj.id)
  if (!colorRank || !tournamentId) return null
  return {
    colorRank,
    tournamentId,
    status: pickStatus(obj.status),
    participantCount: pickNumber(
      obj.participantCount ??
        obj.participant_count ??
        (obj._count && typeof obj._count === "object"
          ? (obj._count as Record<string, unknown>).participants
          : undefined)
    ),
    minParticipants: pickNumber(obj.minParticipants ?? obj.min_participants, 4),
    maxParticipants: pickNumber(obj.maxParticipants ?? obj.max_participants, 16),
    myEntry: normalizeEntry(obj.myEntry ?? obj.my_entry),
  }
}

export function normalizeDailyTournamentCurrent(
  raw: unknown
): DailyTournamentCurrent | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const ranksRaw = Array.isArray(obj.ranks) ? obj.ranks : []
  const ranks = ranksRaw
    .map(normalizeDailyRankSummary)
    .filter((item): item is DailyRankSummary => item != null)

  return {
    period: "daily",
    dayStart: pickString(obj.dayStart ?? obj.day_start),
    registrationOpen: pickBool(obj.registrationOpen ?? obj.registration_open),
    nextStartAt: pickString(obj.nextStartAt ?? obj.next_start_at),
    msUntilStart: pickNumber(obj.msUntilStart ?? obj.ms_until_start),
    currentDayStartAt: pickString(
      obj.currentDayStartAt ?? obj.current_day_start_at
    ),
    ranks,
  }
}

export function normalizeDailyTournamentHistoryItem(
  raw: unknown
): DailyTournamentHistoryItem | null {
  const detail = normalizeVirtualTournamentDetail(raw)
  if (!detail) return null
  const obj = raw as Record<string, unknown>
  const countObj =
    obj._count && typeof obj._count === "object"
      ? (obj._count as Record<string, unknown>)
      : null
  const participantCount = pickNumber(
    countObj?.participants ??
      obj.participantCount ??
      obj.participant_count ??
      detail.participants.length
  )
  return {
    id: detail.id,
    title: detail.title,
    virtualColorRank: detail.virtualColorRank,
    sourceDayStart: pickString(
      obj.sourceDayStart ??
        obj.source_day_start ??
        obj.dayStart ??
        obj.day_start ??
        detail.sourceWeekStart
    ),
    status: pickStatus(obj.status ?? detail.status),
    participants: detail.participants,
    participantCount,
  }
}

export function findMyDailyRegisteredRank(
  ranks: DailyRankSummary[]
): DailyRankSummary | null {
  return ranks.find((rank) => rank.myEntry != null) ?? null
}

export { formatVirtualTournamentRankLabel as formatDailyTournamentRankLabel }
